/**
 * CORE_REGISTRY — Central dispatch for all Excel tool executors.
 *
 * Each entry maps a tool name to a core function that receives the
 * Excel RequestContext and optional arguments. Core functions queue
 * loads/writes on proxy objects but generally do NOT call
 * `context.sync()` themselves — the `executeExcelTool` wrapper handles
 * the final sync. Functions that need intermediate syncs (correlated
 * objects, chunking) manage their own sync calls.
 */

/* global Excel */

import type { ClientToolExecutorAdapter } from "@magnus/react/copilot-tools";
import {
  assertBatchTools,
  type BatchOperation,
  type BatchOperationResult,
  raceWithTimeout,
  shouldSyncAfterOp,
  summarizeBatch,
  sumToolTimeouts,
} from "@/lib/office/run-executor";
import { splitSheetQualifiedAddress } from "../a1";
import {
  captureActiveSheetName,
  clearActiveSheetPin,
  formatOfficeError,
  setActiveSheetPin,
} from "./utils";

// ---------------------------------------------------------------------------
// Category imports
// ---------------------------------------------------------------------------

import {
  clearRangeCore,
  readRangeCore,
  setCellFormulaCore,
  writeRangeCore,
} from "./data-io";
import {
  autoFitCore,
  formatRangeCore,
  mergeCellsCore,
  setBordersCore,
  setDimensionsCore,
} from "./formatting";
import {
  getActiveSheetInfoCore,
  getSheetNamesCore,
  readFormattingCore,
} from "./inspection";
import { renderRangeImageCore } from "./rendering";
import {
  createSheetCore,
  deleteSheetCore,
  renameSheetCore,
} from "./sheet-management";
import {
  addCommentCore,
  addDataValidationCore,
  deleteCommentCore,
  deleteRowsColumnsCore,
  findReplaceCore,
  getCommentsCore,
  insertRowsColumnsCore,
  setConditionalFormattingCore,
  setPageLayoutCore,
} from "./structure-and-misc";
import {
  autoFilterCore,
  createChartCore,
  createTableCore,
  sortRangeCore,
} from "./tables-charts";

// ---------------------------------------------------------------------------
// Core function type
// ---------------------------------------------------------------------------

type CoreFn = (
  ctx: Excel.RequestContext,
  args?: Record<string, unknown>
) => Promise<unknown> | unknown;

// ---------------------------------------------------------------------------
// CORE_REGISTRY
// ---------------------------------------------------------------------------

const CORE_REGISTRY: Record<string, CoreFn> = {
  // Inspection
  get_sheet_names: getSheetNamesCore,
  get_active_sheet_info: getActiveSheetInfoCore,
  read_formatting: readFormattingCore,
  render_range_image: renderRangeImageCore,
  // Data I/O
  read_range: readRangeCore,
  // Same read; the tool-results route saves the result to the chat's
  // outputs (see apps/web/lib/excel/range-spill.ts).
  read_range_to_file: readRangeCore,
  write_range: writeRangeCore,
  // Args arrive pre-hydrated by the workflow (file reference → inline
  // `values`, see apps/web/workflows/chat/steps/hydrate-file-range.ts), so
  // the executor contract is identical to `write_range`.
  write_range_from_file: writeRangeCore,
  set_cell_formula: setCellFormulaCore,
  clear_range: clearRangeCore,
  // Formatting
  format_range: formatRangeCore,
  set_borders: setBordersCore,
  auto_fit: autoFitCore,
  set_dimensions: setDimensionsCore,
  merge_cells: mergeCellsCore,
  // Sheet Management
  create_sheet: createSheetCore,
  delete_sheet: deleteSheetCore,
  rename_sheet: renameSheetCore,
  // Tables & Charts
  create_table: createTableCore,
  create_chart: createChartCore,
  // Data Operations
  sort_range: sortRangeCore,
  auto_filter: autoFilterCore,
  find_replace: findReplaceCore,
  // Structure
  insert_rows_columns: insertRowsColumnsCore,
  delete_rows_columns: deleteRowsColumnsCore,
  // Advanced
  set_conditional_formatting: setConditionalFormattingCore,
  add_data_validation: addDataValidationCore,
  set_page_layout: setPageLayoutCore,
  // Comments
  add_comment: addCommentCore,
  get_comments: getCommentsCore,
  delete_comment: deleteCommentCore,
};

// ---------------------------------------------------------------------------
// Per-tool timeout configuration (ms)
// ---------------------------------------------------------------------------

const TOOL_TIMEOUTS: Record<string, number> = {
  // Inspection — fast reads
  get_sheet_names: 10_000,
  get_active_sheet_info: 15_000,
  read_formatting: 30_000,
  render_range_image: 30_000,
  // Data I/O — potentially large
  read_range: 60_000,
  read_range_to_file: 60_000,
  write_range: 60_000,
  write_range_from_file: 60_000,
  set_cell_formula: 15_000,
  clear_range: 15_000,
  // Formatting — generally fast
  format_range: 15_000,
  set_borders: 15_000,
  auto_fit: 15_000,
  set_dimensions: 15_000,
  merge_cells: 10_000,
  // Sheet Management — fast
  create_sheet: 10_000,
  delete_sheet: 10_000,
  rename_sheet: 10_000,
  // Tables & Charts
  create_table: 30_000,
  create_chart: 30_000,
  // Data Operations
  sort_range: 30_000,
  auto_filter: 15_000,
  find_replace: 60_000, // can be slow on large ranges
  // Structure
  insert_rows_columns: 15_000,
  delete_rows_columns: 15_000,
  // Advanced
  set_conditional_formatting: 15_000,
  add_data_validation: 15_000,
  set_page_layout: 10_000,
  // Comments
  add_comment: 15_000,
  get_comments: 30_000,
  delete_comment: 10_000,
};
const DEFAULT_TIMEOUT = 60_000;

// ---------------------------------------------------------------------------
// Self-syncing tools — these call `ctx.sync()` internally (correlated-
// objects pattern, chunked reads, etc.).  When used in a batch, the
// loop does NOT need an additional sync after them because their own
// internal sync also flushes any prior queued proxy operations from
// preceding queue-only tools.
//
// Everything NOT in this set is "queue-only" — it just assigns
// properties on proxy objects and relies on a later sync to flush.
// ---------------------------------------------------------------------------

const SELF_SYNCING_TOOLS: ReadonlySet<string> = new Set([
  "get_sheet_names",
  "get_active_sheet_info",
  "read_range",
  "read_range_to_file",
  "read_formatting",
  "render_range_image",
  "create_sheet",
  "create_table",
  "create_chart",
  "find_replace",
  "add_comment",
  "get_comments",
]);

// Approximate upper limit for a single sync payload (Office.js ~5 MB).
// We check write_range payloads in the batch to fail fast instead of
// producing a confusing timeout.
const MAX_PAYLOAD_BYTES = 4_500_000; // ~4.5 MB safety margin

// Tools that must NOT run with screen updating suspended. `Range.getImage()`
// captures whatever is currently *painted*; under
// `suspendScreenUpdatingUntilNextSync()` a region whose paint was invalidated
// by a preceding mutation (set_dimensions, insert/delete rows, etc.) is
// captured blank because the repaint is deferred. Rendering must let the grid
// repaint, so we skip screen-update suspension for it. (The first render of a
// turn looked fine only because that region happened to already be painted
// on-screen.)
const SCREEN_UPDATE_EXEMPT_TOOLS: ReadonlySet<string> = new Set([
  "render_range_image",
]);

// ---------------------------------------------------------------------------
// Active-sheet pin policy
// ---------------------------------------------------------------------------
//
// We capture the active sheet name up front and pin it (see ./utils) so a
// user switching worksheets mid-execution can't redirect queued writes. The
// capture costs one extra sync, so we skip it when it can't help: tools that
// operate at the workbook level or address a sheet by an explicit required
// name never consult the active-sheet fallback, and an op that already names
// its target sheet doesn't need the pin either.

const PIN_EXEMPT_TOOLS: ReadonlySet<string> = new Set([
  "get_sheet_names",
  "create_sheet",
  "delete_sheet",
  "rename_sheet",
]);

const ADDRESS_ARGS = [
  "range",
  "dataRange",
  "cellAddress",
  "printArea",
] as const;

/**
 * True when args already name a target sheet: `sheetName` (or `sheet` for
 * inspection), or a sheet-qualified address such as `"FARS!A1:G30"`.
 */
function hasExplicitSheet(args: Record<string, unknown>): boolean {
  if (typeof args.sheetName === "string" || typeof args.sheet === "string") {
    return true;
  }
  return ADDRESS_ARGS.some((key) => {
    const value = args[key];
    // Multi-area addresses aren't routed by their prefix (see setPageLayout).
    return (
      typeof value === "string" &&
      !value.includes(",") &&
      splitSheetQualifiedAddress(value).sheetName !== undefined
    );
  });
}

/** Whether a given tool call benefits from pinning the active sheet. */
function shouldPinActiveSheet(
  toolName: string,
  args: Record<string, unknown>
): boolean {
  return !PIN_EXEMPT_TOOLS.has(toolName) && !hasExplicitSheet(args);
}

// Above this op count we skip screen-update suspension for a batch. Suspending
// screen updates across a long all-queue batch (e.g. recreating many cells)
// freezes the grid for the whole run, so a mid-batch sheet switch *looks* like
// a hang. Larger batches keep the grid live (minor flicker) so the user stays
// oriented; small batches keep the smooth suspended path.
const SCREEN_UPDATE_SUSPEND_MAX_OPS = 8;

/**
 * Best-effort runtime recovery after a timeout. A timed-out `Excel.run()` is
 * NOT cancelled by losing the `Promise.race` — Office.js has no cancellation —
 * so if it left `runtime.enableEvents = false`, host events stay dead and the
 * grid can appear frozen. Re-enabling events and syncing restores
 * interactivity. Fire-and-forget: if the original run is genuinely wedged this
 * queues behind it and applies once it clears, and it's never awaited so the
 * caller's error surfaces promptly.
 */
async function recoverExcelRuntime(): Promise<void> {
  try {
    await Excel.run(async (context) => {
      context.runtime.enableEvents = true;
      await context.sync();
    });
  } catch {
    // Nothing more we can do — swallow.
  }
}

// ---------------------------------------------------------------------------
// Exported tool name set — used by the Excel adapter to claim tools
// ---------------------------------------------------------------------------

/** All tool names handled by the Excel executor (derived from CORE_REGISTRY). */
export const EXCEL_TOOL_NAMES: ReadonlySet<string> = new Set([
  ...Object.keys(CORE_REGISTRY),
  "batch_operations",
]);

/**
 * Excel adapter for the host-neutral client-side tool executor.
 * Provide via `useCopilotTools(messages, { adapters: [excelClientToolAdapter] })`
 * on the Excel add-in surface.
 *
 * @see ADR-043 — Host-Neutral Client-Side Tool Protocol
 */
export const excelClientToolAdapter: ClientToolExecutorAdapter = {
  execute: executeExcelTool,
  toolNames: EXCEL_TOOL_NAMES,
};

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Execute an Excel tool by name.
 *
 * Wraps the core function in `Excel.run()` with:
 * - **Suspended calculation** — recalc deferred until the final sync
 * - **Suspended screen updating** — grid repaints deferred until the final sync
 * - **Disabled events** — prevents competing `Excel.run()` calls from event
 *   handlers during tool execution
 * - **Timeout guard** — prevents indefinite hangs when Excel is busy
 * - **Error formatting** — normalises Office.js errors into readable messages
 *
 * Core functions receive the live `RequestContext` and queue operations on
 * proxy objects. A final `context.sync()` is issued after the core function
 * returns (core functions that need earlier syncs — e.g. correlated-objects
 * pattern — do their own).
 */
export async function executeExcelTool(
  toolName: string,
  args: Record<string, unknown>,
  // toolCallId is part of the adapter contract for HITL wrapping; the
  // Excel executor itself doesn't need it.
  _toolCallId?: string
): Promise<unknown> {
  // ─── Batch operations ────────────────────────────────────────────
  if (toolName === "batch_operations") {
    return executeBatchOperations(args);
  }

  const coreFn = CORE_REGISTRY[toolName];
  if (!coreFn) {
    throw new Error(`Unknown Excel tool: ${toolName}`);
  }

  // Verify Office.js is available
  if (typeof Excel === "undefined" || !Excel.run) {
    throw new Error(
      "Excel API is not available. Ensure you are running inside the Excel add-in."
    );
  }

  const timeout = TOOL_TIMEOUTS[toolName] ?? DEFAULT_TIMEOUT;
  const needsPin = shouldPinActiveSheet(toolName, args);
  const suspendScreen = !SCREEN_UPDATE_EXEMPT_TOOLS.has(toolName);

  const excelPromise = Excel.run(async (context) => {
    // Perf: suspend recalculation until the final sync
    context.application.suspendApiCalculationUntilNextSync();
    // Perf: suspend grid repaints until the final sync — but NOT for tools that
    // need the grid painted (e.g. render_range_image's getImage()).
    if (suspendScreen) {
      context.application.suspendScreenUpdatingUntilNextSync();
    }
    // Perf: disable events to prevent competing Excel.run() calls
    context.runtime.enableEvents = false;

    // Pin the active sheet so a mid-flight worksheet switch can't redirect
    // this write to the wrong sheet.
    let pinToken: number | undefined;
    if (needsPin) {
      pinToken = setActiveSheetPin(await captureActiveSheetName(context));
    }

    try {
      const result = await coreFn(context, args);
      return result;
    } finally {
      if (pinToken !== undefined) {
        clearActiveSheetPin(pinToken);
      }
      // Re-enable events before the final sync so the assignment is
      // queued on the proxy object and takes effect during sync.
      context.runtime.enableEvents = true;
      await context.sync();
    }
  });

  try {
    return await raceWithTimeout(
      excelPromise,
      timeout,
      `Excel tool "${toolName}" timed out after ${timeout / 1000}s. ` +
        "Excel may be busy — close any dialogs and try again."
    );
  } catch (err) {
    // Any failure here — a timeout, an aborted/cancelled run whose `Excel.run`
    // never reached its `finally`, or a thrown Office error — may have left
    // `runtime.enableEvents = false`, freezing the grid. Recovery just
    // re-enables events and syncs, so it's a harmless no-op when the run
    // already cleaned up after itself. Fire-and-forget, so the caller's error
    // surfaces promptly.
    void recoverExcelRuntime();
    throw new Error(formatOfficeError(err));
  }
}

// ---------------------------------------------------------------------------
// Batch Operations
// ---------------------------------------------------------------------------

/**
 * Execute multiple Excel operations in a single `Excel.run()` context.
 *
 * This is dramatically faster than individual tool calls because:
 * 1. Only one Excel.run() context creation + sync cycle
 * 2. Suspended calculation/screen updating across ALL operations
 * 3. Only one webhook round-trip instead of N
 * 4. Reduces workflow replay overhead from O(N²) to O(1)
 *
 * **Sync strategy (per Microsoft best-practices article):**
 *
 * Tools are classified as *self-syncing* (they call `ctx.sync()` internally
 * — e.g. reads, creates that load+sync) or *queue-only* (they just set
 * properties on proxy objects).  The loop avoids the naïve per-iteration
 * `context.sync()` pattern the article warns about:
 *
 *  • Self-syncing tools — no additional sync needed.  Their internal sync
 *    also flushes any prior queued proxy operations from preceding
 *    queue-only tools.
 *  • Consecutive queue-only tools — operations accumulate on the context
 *    without syncing.  A single sync is issued when the run of queue-only
 *    tools ends (i.e. before the next self-syncing tool, or at the end of
 *    the batch).
 *
 * This can reduce a 12-operation batch from 13 syncs to as few as 2–3.
 *
 * If an operation fails, subsequent operations still execute (fail-forward)
 * — the error is captured per-operation in the results.
 */
async function executeBatchOperations(
  args: Record<string, unknown>
): Promise<{ results: BatchOperationResult[]; summary: string }> {
  const operations = (args.operations ?? []) as BatchOperation[];

  if (operations.length === 0) {
    return { results: [], summary: "No operations provided." };
  }

  // Validate all tool names up front (no nesting, all known)
  assertBatchTools(operations, CORE_REGISTRY, "Excel");

  // File-reference args are hydrated by the workflow only on the standalone
  // dispatch path — inside a batch they'd arrive unhydrated (no `values`).
  if (operations.some((op) => op.tool === "write_range_from_file")) {
    throw new Error(
      "write_range_from_file cannot be used inside batch_operations — call it standalone."
    );
  }
  // Likewise the file save happens only on the standalone result path.
  if (operations.some((op) => op.tool === "read_range_to_file")) {
    throw new Error(
      "read_range_to_file cannot be used inside batch_operations — call it standalone."
    );
  }

  if (typeof Excel === "undefined" || !Excel.run) {
    throw new Error(
      "Excel API is not available. Ensure you are running inside the Excel add-in."
    );
  }

  // Use a generous timeout for batch — sum of individual timeouts, capped
  const totalTimeout = sumToolTimeouts(
    operations,
    TOOL_TIMEOUTS,
    DEFAULT_TIMEOUT
  );

  // Keep the grid live for large batches: suspending screen updates across a
  // long all-queue run makes a mid-batch sheet switch look like a freeze. Also
  // never suspend when a render op is present — `getImage()` captures blank if
  // the grid hasn't repainted (see SCREEN_UPDATE_EXEMPT_TOOLS).
  const suspendScreen =
    operations.length <= SCREEN_UPDATE_SUSPEND_MAX_OPS &&
    !operations.some((op) => SCREEN_UPDATE_EXEMPT_TOOLS.has(op.tool));
  // Pin the active sheet if any op would otherwise resolve it live at sync.
  const needsPin = operations.some((op) =>
    shouldPinActiveSheet(op.tool, op.args ?? {})
  );

  const excelPromise = Excel.run(async (context) => {
    context.application.suspendApiCalculationUntilNextSync();
    if (suspendScreen) {
      context.application.suspendScreenUpdatingUntilNextSync();
    }
    context.runtime.enableEvents = false;

    let pinToken: number | undefined;
    if (needsPin) {
      pinToken = setActiveSheetPin(await captureActiveSheetName(context));
    }

    const results: BatchOperationResult[] = [];

    try {
      for (let i = 0; i < operations.length; i++) {
        const op = operations[i]!;
        const coreFn = CORE_REGISTRY[op.tool]!;

        try {
          // ── Payload size guard for large writes ──────────────
          if (op.tool === "write_range" && op.args?.values) {
            const estimated = JSON.stringify(op.args.values).length;
            if (estimated > MAX_PAYLOAD_BYTES) {
              results.push({
                tool: op.tool,
                status: "error",
                error:
                  `Payload too large (~${(estimated / 1_000_000).toFixed(1)} MB). ` +
                  "Office.js limits sync payloads to ~5 MB. Split into smaller writes.",
              });
              continue;
            }
          }

          const result = await coreFn(context, op.args ?? {});

          // ── Smart sync strategy ─────────────────────────────
          // Self-syncing tools already called ctx.sync() internally (which
          // also flushed any prior queued proxy operations). Queue-only tools
          // sync only when the run of queue-only tools ends (before the next
          // self-syncing tool, or at batch end).
          if (shouldSyncAfterOp(i, operations, SELF_SYNCING_TOOLS)) {
            await context.sync();
          }

          results.push({ tool: op.tool, status: "success", result });
        } catch (opErr) {
          // Capture the error but continue with remaining operations
          results.push({
            tool: op.tool,
            status: "error",
            error: formatOfficeError(opErr),
          });
          // Re-suspend calculation after error (safe to re-call).
          // Do NOT re-call suspendScreenUpdatingUntilNextSync() —
          // Microsoft docs warn repeated calls cause window flicker.
          try {
            context.application.suspendApiCalculationUntilNextSync();
          } catch {
            // If re-suspend fails, continue anyway
          }
        }
      }
    } finally {
      if (pinToken !== undefined) {
        clearActiveSheetPin(pinToken);
      }
      context.runtime.enableEvents = true;
      await context.sync();
    }

    return results;
  });

  try {
    const results = await raceWithTimeout(
      excelPromise,
      totalTimeout,
      `Batch operations timed out after ${totalTimeout / 1000}s. ` +
        "Excel may be busy — close any dialogs and try again."
    );
    return { results, summary: summarizeBatch(results) };
  } catch (err) {
    // Any failure (timeout, abort/cancel, or thrown error) may have left events
    // disabled mid-run; recover the host runtime so the grid doesn't stay
    // frozen. Harmless no-op when the run already cleaned up. Fire-and-forget.
    void recoverExcelRuntime();
    throw new Error(formatOfficeError(err));
  }
}
